// The in-app site review: the owner taps elements of their LIVE Symphony-built site (in an iframe here), leaves a
// comment on each, and "Finish review" puts ONE message into the chat composer, which the owner sends. Nothing is sent
// per comment. Contract: VMR `ai-scheduling-assistant/individual/docs/2026-10-24-site-edit-and-review-contract.md` §2.
//
// Pure and import-free (unit-tested in `test/site-review.test.ts`). The SAME rules, by the same names, are on both
// phones: iOS `SiteReview.swift` (`SiteEditRules`), Android `SiteReview.kt`. Change one, change all three.

/** The query parameter that switches a published page's review bridge on. */
export const REVIEW_PARAM = 'symphony-review';

/** Origins the published bridge accepts a `hello` from (`SiteCli.REVIEW_ORIGINS`). Elsewhere (localhost) review never starts. */
export const REVIEW_HOST_ORIGINS: readonly string[] = [
  'https://symphony.wix.app',
  'https://symphony.wix.com',
  'https://www.symphony.wix.com',
  'https://chat.nownia.com',
  'https://app.nownia.com',
];

/** How long the web host repeats `hello` before it shows the preview without review. */
export const REVIEW_HELLO_INTERVAL_MS = 500;
export const REVIEW_HELLO_TIMEOUT_MS = 10_000;

const MAX_URL = 2000;
const MAX_SELECTOR = 1000;
const MAX_TEXT = 200;
const MAX_TAG = 40;
const MAX_TITLE = 200;

function httpUrl(raw: unknown): URL | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value.length > MAX_URL) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/** An http(s) url as given, or '' — the only addresses the review ever loads or opens. */
export function reviewHref(raw: string | undefined | null): string {
  return httpUrl(raw) ? (raw ?? '').trim() : '';
}

/**
 * The preview url in review mode: `symphony-review=1` added, keeping any query and fragment, never duplicated.
 * '' for anything that is not an http(s) url.
 */
export function reviewUrl(previewUrl: string): string {
  const url = httpUrl(previewUrl);
  if (!url) return '';
  if (url.searchParams.has(REVIEW_PARAM)) return url.toString();
  // Appended by hand rather than through `searchParams`, which would re-encode the query the site already has.
  url.search = url.search && url.search !== '?' ? `${url.search}&${REVIEW_PARAM}=1` : `?${REVIEW_PARAM}=1`;
  return url.toString();
}

/** The page's address without the fragment and without the review param — what two pages are compared by. */
export function normalizePageUrl(pageUrl: string): string {
  const url = httpUrl(pageUrl);
  if (!url) return pageUrl.trim();
  url.hash = '';
  url.searchParams.delete(REVIEW_PARAM);
  return url.toString();
}

/** The scheme + host + port of an http(s) url, or ''. */
export function urlOrigin(raw: string): string {
  return httpUrl(raw)?.origin ?? '';
}

/* ---------------------------------------------------------------------------------------------- messages */

export interface ReviewRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ReviewReady {
  type: 'symphony-review:ready';
  pageUrl: string;
  title: string;
}

export interface ReviewPick {
  type: 'symphony-review:pick';
  pageUrl: string;
  selector: string;
  text: string;
  tag: string;
  rect: ReviewRect | null;
  viewport: { w: number; h: number } | null;
  devicePixelRatio: number;
}

export type ReviewMessage = ReviewReady | ReviewPick;

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Whitespace (CR/LF/tab included) collapsed to single spaces, trimmed, capped. */
function oneLine(value: unknown, cap: number): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim().slice(0, cap);
}

function rectOf(value: unknown): ReviewRect | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  return finite(r.x) && finite(r.y) && finite(r.w) && finite(r.h) ? { x: r.x, y: r.y, w: r.w, h: r.h } : null;
}

function viewportOf(value: unknown): { w: number; h: number } | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  return finite(v.w) && finite(v.h) ? { w: v.w, h: v.h } : null;
}

/**
 * A page → host message, or null. Every field is untrusted text from a web page: re-validated and re-capped here.
 * Web passes `event.data` (an object); a JSON string is parsed too (the phones receive one).
 */
export function parseReviewMessage(raw: unknown): ReviewMessage | null {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const m = data as Record<string, unknown>;
  const page = httpUrl(m.pageUrl);
  if (!page) return null;
  const pageUrl = (m.pageUrl as string).trim();
  if (m.type === 'symphony-review:ready') {
    return { type: 'symphony-review:ready', pageUrl, title: oneLine(m.title, MAX_TITLE) };
  }
  if (m.type === 'symphony-review:pick') {
    if (typeof m.selector !== 'string') return null;
    const selector = m.selector.trim().slice(0, MAX_SELECTOR);
    if (!selector) return null;
    const tag = oneLine(m.tag, MAX_TAG).toLowerCase() || 'element';
    return {
      type: 'symphony-review:pick',
      pageUrl,
      selector,
      text: oneLine(m.text, MAX_TEXT),
      tag,
      rect: rectOf(m.rect),
      viewport: viewportOf(m.viewport),
      devicePixelRatio: finite(m.devicePixelRatio) && m.devicePixelRatio > 0 ? m.devicePixelRatio : 1,
    };
  }
  return null;
}

/**
 * The web host's guard: a message counts only when it comes from THIS iframe (`fromFrame`: the event's source is
 * its contentWindow) and from the preview's own origin. Any other window or origin is ignored.
 */
export function acceptReviewEvent(event: { origin: string; fromFrame: boolean }, previewUrl: string): boolean {
  const origin = urlOrigin(previewUrl);
  return event.fromFrame && origin !== '' && event.origin === origin;
}

/** The app is served from an origin the bridge answers (contract §2.3). Elsewhere the hello is never sent. */
export function reviewHostAllowed(origin: string): boolean {
  return REVIEW_HOST_ORIGINS.includes(origin);
}

/* ---------------------------------------------------------------------------------------------- comments */

export interface ReviewComment {
  pageUrl: string;
  selector: string;
  text: string;
  tag: string;
  comment: string;
}

export interface ReviewPin {
  selector: string;
  label: string;
}

/** The pins to show on `pageUrl`: every comment on that page, labelled with its 1-based number in the whole review. */
export function pinsForPage(comments: readonly ReviewComment[], pageUrl: string): ReviewPin[] {
  const page = normalizePageUrl(pageUrl);
  const pins: ReviewPin[] = [];
  comments.forEach((c, i) => {
    if (normalizePageUrl(c.pageUrl) === page) pins.push({ selector: c.selector, label: String(i + 1) });
  });
  return pins;
}

/** The host → page messages (contract §2.3). */
export function modeMessage(picking: boolean) {
  return { type: 'symphony-review:mode', picking } as const;
}

export function highlightMessage(pins: ReviewPin[], scroll = false) {
  return scroll ? ({ type: 'symphony-review:highlight', pins, scroll: true } as const) : ({ type: 'symphony-review:highlight', pins } as const);
}

export const HELLO_MESSAGE = { type: 'symphony-review:hello' } as const;

/**
 * The "finished review" composer message (contract §2.5), EXACT: the localized lead, a blank line, then one block per
 * comment in review order, blocks separated by one blank line, no trailing newline.
 *
 *     1. <pageUrl>
 *        Element: <tag> "<text>" (<selector>)
 *        Comment: <comment line 1>
 *        <comment line 2>
 */
export function reviewMessage(lead: string, comments: readonly ReviewComment[]): string {
  const blocks = comments.map((c, i) => {
    const text = oneLine(c.text, MAX_TEXT).replace(/"/g, "'");
    const tag = oneLine(c.tag, MAX_TAG) || 'element';
    const selector = oneLine(c.selector, MAX_SELECTOR);
    const element = text ? `${tag} "${text}" (${selector})` : `${tag} (${selector})`;
    const [first = '', ...rest] = c.comment.replace(/\r\n?/g, '\n').trim().split('\n');
    const comment = [`   Comment: ${first}`, ...rest.map((line) => `   ${line}`)].join('\n');
    return `${i + 1}. ${oneLine(c.pageUrl, MAX_URL)}\n   Element: ${element}\n${comment}`;
  });
  return [lead.trim(), ...blocks].join('\n\n');
}

/** The key a review is kept under: per site, per conversation (a new chat not yet created counts as its Space). */
export function reviewKey(previewUrl: string, conversationId: string | null, workspaceId: string): string {
  return `${urlOrigin(previewUrl)}|${conversationId ?? `space:${workspaceId}`}`;
}

/** "host/path" of a page, for the pane's address line and a comment on another page. */
export function pageLabel(pageUrl: string): string {
  const url = httpUrl(pageUrl);
  if (!url) return pageUrl;
  const path = url.pathname === '/' ? '' : url.pathname;
  return `${url.host}${path}`;
}

/* ---------------------------------------------------------------------------------------------- what the owner reads */

/**
 * What kind of thing a picked element is, for the OWNER's eyes only: `h1` reads as a word ("a") to someone who does
 * not write HTML. The view localizes it as `siteReview.element.<kind>`. The finished-review message keeps the raw
 * `tag` (contract §2.5) — that line is for the agent.
 */
export type ElementKind = 'heading' | 'link' | 'button' | 'image' | 'text' | 'section' | 'field' | 'video' | 'element';

const ELEMENT_KINDS: Record<string, ElementKind> = {
  h1: 'heading', h2: 'heading', h3: 'heading', h4: 'heading', h5: 'heading', h6: 'heading',
  a: 'link',
  button: 'button', summary: 'button',
  img: 'image', picture: 'image', svg: 'image', canvas: 'image', figure: 'image',
  p: 'text', span: 'text', li: 'text', label: 'text', strong: 'text', em: 'text', b: 'text', i: 'text', small: 'text',
  blockquote: 'text', figcaption: 'text', td: 'text', th: 'text', dt: 'text', dd: 'text',
  section: 'section', div: 'section', main: 'section', header: 'section', footer: 'section', nav: 'section',
  article: 'section', aside: 'section', form: 'section', ul: 'section', ol: 'section',
  input: 'field', textarea: 'field', select: 'field',
  video: 'video',
};

export function elementKind(tag: string): ElementKind {
  const key = tag.trim().toLowerCase();
  // Own keys only: a page can send any tag string, and `constructor` must not read Object.prototype.
  return Object.prototype.hasOwnProperty.call(ELEMENT_KINDS, key) ? ELEMENT_KINDS[key] : 'element';
}

/** The catalog key each kind reads as (spelled out, so the catalog guard sees every key). */
export const ELEMENT_KIND_KEYS: Record<ElementKind, string> = {
  heading: 'siteReview.element.heading',
  link: 'siteReview.element.link',
  button: 'siteReview.element.button',
  image: 'siteReview.element.image',
  text: 'siteReview.element.text',
  section: 'siteReview.element.section',
  field: 'siteReview.element.field',
  video: 'siteReview.element.video',
  element: 'siteReview.element.element',
};

/** `Heading "Slow down…"`, or the kind alone when the element has no text. `kindLabel` is the localized kind. */
export function ownerElementLabel(kindLabel: string, text: string): string {
  return text ? `${kindLabel} "${text}"` : kindLabel;
}

/** The page's path for the owner ("/about.html"; "/" for the home page), decoded; the input itself when not a url. */
export function pagePath(pageUrl: string): string {
  const url = httpUrl(pageUrl);
  if (!url) return pageUrl.trim();
  let path = url.pathname || '/';
  try {
    path = decodeURIComponent(path);
  } catch {
    // A malformed escape stays as sent.
  }
  return path || '/';
}

/* ---------------------------------------------------------------------------------------------- the sent review */

/**
 * The lead line (`siteReview.finishMessage.lead`) in every locale the app ships. A sent review starts with whichever
 * language the owner's app was in, and any reader (another device, another language) must still recognise it.
 * `test/site-review.test.ts` pins this list to the 37 catalogs, so a changed translation fails there, not in a chat.
 */
export const REVIEW_LEADS: readonly string[] = [
  'عالج جميع التعليقات على الموقع', // ar
  'Отрази всички коментари по сайта', // bg
  'Resol tots els comentaris del lloc', // ca
  'Zapracuj všechny komentáře k webu', // cs
  'Håndter alle kommentarer på webstedet', // da
  'Setze alle Kommentare zur Website um', // de
  'Εφάρμοσε όλα τα σχόλια για τον ιστότοπο', // el
  'Address all comments on the site', // en
  'Atiende todos los comentarios del sitio', // es
  'Käsittele kaikki sivuston kommentit', // fi
  'Traitez tous les commentaires sur le site', // fr
  'לטפל בכל ההערות על האתר', // he
  'साइट पर की गई सभी टिप्पणियों पर काम करें', // hi
  'Riješi sve komentare na stranici', // hr
  'Dolgozd fel a webhely összes megjegyzését', // hu
  'Tangani semua komentar di situs', // id
  'Risolvi tutti i commenti sul sito', // it
  'サイトへのコメントをすべて反映してください', // ja
  '사이트의 모든 댓글을 반영해 주세요', // ko
  'Atsižvelk į visus komentarus apie svetainę', // lt
  'Ņem vērā visus komentārus par vietni', // lv
  'Tangani semua komen pada laman', // ms
  'Verwerk alle opmerkingen op de site', // nl
  'Følg opp alle kommentarene på nettstedet', // no
  'Uwzględnij wszystkie komentarze na stronie', // pl
  'Resolva todos os comentários do site', // pt
  'Rezolvă toate comentariile de pe site', // ro
  'Обработай все комментарии к сайту', // ru
  'Zapracuj všetky komentáre k webu', // sk
  'Upoštevaj vse komentarje na spletnem mestu', // sl
  'Åtgärda alla kommentarer på webbplatsen', // sv
  'จัดการความคิดเห็นทั้งหมดบนเว็บไซต์', // th
  'Ayusin ang lahat ng komento sa site', // tl
  'Sitedeki tüm yorumları ele al', // tr
  'Опрацюй усі коментарі до сайту', // uk
  'Xử lý tất cả bình luận trên trang web', // vi
  '处理网站上的所有评论', // zh
];

/** One comment read back out of a sent review message. */
export interface SentReviewComment {
  pageUrl: string;
  tag: string;
  text: string;
  selector: string;
  comment: string;
}

const HEADER_LINE = /^(\d+)\. (\S+)$/;
const ELEMENT_LINE = /^ {3}Element: (\S+)(?: "([^"]*)")? \((.*)\)$/;
const COMMENT_LINE = /^ {3}Comment: (.*)$/;

/**
 * The finished-review message (contract §2.5, `reviewMessage`) read back, for the chat to draw as a card. RENDER only:
 * the stored text is never changed. Null unless the WHOLE text is that format — the lead (any locale in `leads`), a
 * blank line, then blocks numbered 1, 2, … in order, each a page url, its Element line, its Comment line and comment
 * lines indented by three spaces, one blank line between blocks. Anything else (an owner who typed above the review,
 * an edited block) stays a plain bubble.
 */
export function parseSentReview(message: string, leads: readonly string[] = REVIEW_LEADS): SentReviewComment[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines.length < 5 || !leads.includes(lines[0].trim()) || lines[1].trim() !== '') return null;
  const comments: SentReviewComment[] = [];
  let i = 2;
  while (i < lines.length) {
    const header = HEADER_LINE.exec(lines[i]);
    if (!header || Number(header[1]) !== comments.length + 1 || !httpUrl(header[2])) return null;
    const element = ELEMENT_LINE.exec(lines[i + 1] ?? '');
    const first = COMMENT_LINE.exec(lines[i + 2] ?? '');
    if (!element || !first) return null;
    const comment = [first[1]];
    i += 3;
    while (i < lines.length && lines[i] !== '') {
      if (!lines[i].startsWith('   ')) return null;
      comment.push(lines[i].slice(3));
      i += 1;
    }
    comments.push({ pageUrl: header[2], tag: element[1], text: element[2] ?? '', selector: element[3], comment: comment.join('\n') });
    if (i < lines.length) i += 1; // the blank line between blocks
  }
  return comments.length > 0 ? comments : null;
}

/** "N comment(s)" on the sent-review card: the locale's own plural rule; only `one` and `other` are catalogued. */
export function sentReviewCountKey(count: number, locale: string): 'siteReview.sent.count.one' | 'siteReview.sent.count.other' {
  let form: string = count === 1 ? 'one' : 'other';
  try {
    form = new Intl.PluralRules(locale).select(count);
  } catch {
    // An unknown locale keeps the English rule.
  }
  return form === 'one' ? 'siteReview.sent.count.one' : 'siteReview.sent.count.other';
}

/* ---------------------------------------------------------------------------------------------- Finish sends */

/**
 * Where Finish SENDS the review (decision 2026-10-24: Finish sends; it no longer fills a composer). The review always
 * belongs to the site's chat: the chat it was opened beside, else the newest chat bound to this site, else a NEW chat
 * of the Space — the one Edit (CHAT) opens, bound to the site once it exists. Never "whatever composer is mounted".
 */
export type FinishTarget = { kind: 'existing'; conversationId: string } | { kind: 'new' };

export function finishTarget(openedFrom: string | null | undefined, bound: string | null | undefined): FinishTarget {
  const id = (openedFrom ?? '').trim() || (bound ?? '').trim();
  return id ? { kind: 'existing', conversationId: id } : { kind: 'new' };
}

/**
 * Whether a sent review has landed, read off the chat it went to: still `pending` until that chat has started the send
 * and finished its turn; then `sent` when the owner's message is in it, else `failed` — the send was rolled back, and
 * the comments stay so nothing the owner wrote is lost.
 */
export function reviewSendOutcome(o: {
  text: string;
  userTexts: readonly string[];
  streaming: boolean;
  sawStreaming: boolean;
}): 'pending' | 'sent' | 'failed' {
  if (o.streaming || !o.sawStreaming) return 'pending';
  const text = o.text.trim();
  return o.userTexts.some((t) => t.trim() === text) ? 'sent' : 'failed';
}

/* ---------------------------------------------------------------------------------------------- the pick stays in sight */

/** Breathing room kept between a revealed pick and the edge of what is visible (the comment sheet's top, the toolbar). */
export const REVIEW_REVEAL_MARGIN = 16;

/**
 * How far the page must move UP (positive) or DOWN (negative) so the picked element sits fully inside the part of the
 * preview the owner can still see — on a phone, the strip above the comment sheet (and above the keyboard once it
 * opens). Everything is in the preview's own viewport coordinates: `rect` is the pick's (`ReviewPick.rect`), and
 * `visible` is the band of that viewport left uncovered. 0 when the element is already in sight. An element taller than
 * the band is aligned to its top, so its start is what shows.
 *
 * Web moves the iframe by it (a cross-origin page cannot be scrolled from outside); the phones scroll their web view by
 * it (iOS `SiteEditRules.revealDelta`, Android `revealDelta`). Change one, change all three.
 */
export function revealDelta(rect: ReviewRect, visible: { top: number; bottom: number }, margin = REVIEW_REVEAL_MARGIN): number {
  const top = visible.top + margin;
  const bottom = visible.bottom - margin;
  if (bottom <= top) return rect.y - visible.top;
  if (rect.h >= bottom - top) return rect.y - top;
  if (rect.y + rect.h > bottom) return rect.y + rect.h - bottom;
  if (rect.y < top) return rect.y - top;
  return 0;
}
